import { createContext, FCWithChildren, useCallback, useContext, useMemo } from 'react';
import { BusinessEntity } from '../models/contentPage';
import { IPermission, PermissionType } from '../models/role';
import roleApi from '../redux/apis/roleApi';
import { getEnvSettings } from '../app/hostBasedSettings';
import { SettingsContext } from './SettingsProvider';

const { environment } = getEnvSettings();

export type BusinessEntityRequirements = BusinessEntity | { anyOf: BusinessEntityRequirements[] } | { allOf: BusinessEntityRequirements[] };

export function canAccess(beRequirements: BusinessEntityRequirements, permissionType: PermissionType, userPermissions: IPermission[]): boolean {
  if (typeof beRequirements === 'string') {
    return !!userPermissions.find(p => p.businessEntity === beRequirements && p.permissionType === permissionType);
  }
  else {
    if ('anyOf' in beRequirements)
      return beRequirements.anyOf.some(req => canAccess(req, permissionType, userPermissions));
    else
      return beRequirements.allOf.every(req => canAccess(req, permissionType, userPermissions));
  }
}

interface IPageAccess {
  hasReadAccess: boolean;
  hasUpdateAccess: boolean;
  hasCreateAccess: boolean;
  hasDeleteAccess: boolean;
}

interface ContextValue {
  getPermissions(businessEntity: BusinessEntityRequirements): IPageAccess;
  isLoading: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
export const PageAccessContext = createContext<ContextValue>(undefined!);

const PageAccessProvider: FCWithChildren = ({ children }) => {
  const { settings: { permissionOverrides } } = useContext(SettingsContext);

  const { data: myRole, isLoading } = roleApi.endpoints.getMyRole.useQueryState();
  const canAccessOverrideWithRole = !!myRole && myRole?.id === -1;

  const myPermissions = useMemo(
    () => (permissionOverrides.enabled && (environment === 'dev' || environment === 'qa') && canAccessOverrideWithRole)
      ? permissionOverrides.permissions
      : myRole?.permissions ?? [],
    [canAccessOverrideWithRole, myRole?.permissions, permissionOverrides.enabled, permissionOverrides.permissions],
  );

  const getPermissions = useCallback(
    (requirements: BusinessEntityRequirements): IPageAccess => ({
      hasReadAccess: canAccess(requirements, PermissionType.Read, myPermissions),
      hasUpdateAccess: canAccess(requirements, PermissionType.Update, myPermissions),
      hasCreateAccess: canAccess(requirements, PermissionType.Create, myPermissions),
      hasDeleteAccess: canAccess(requirements, PermissionType.Delete, myPermissions),
    }),
    [myPermissions],
  );

  const value = useMemo(
    () => ({ getPermissions, isLoading }),
    [getPermissions, isLoading],
  );

  return <PageAccessContext.Provider value={value}>
    {children}
  </PageAccessContext.Provider>;
};

export default PageAccessProvider;

export function useGetPermissions(beRequirements: BusinessEntityRequirements): IPageAccess {
  const { getPermissions } = useContext(PageAccessContext);

  const permissions = useMemo(
    () => getPermissions(beRequirements),
    [beRequirements, getPermissions],
  );

  return permissions;
}